import { lock, clearBodyLocks } from "tua-body-scroll-lock";
import { IAccountOverlay } from "@snippets/account-overlay";

class DesktopMenu extends HTMLElement {
  private menuItems: HTMLElement[];
  private topLevelParents: HTMLElement[];
  private dropdownToggle: HTMLElement[];
  private navigationImageLinks: HTMLElement[];
  private globalOverlay: HTMLElement | null;

  constructor() {
    super();
    this.menuItems = [];
    this.topLevelParents = [];
    this.dropdownToggle = [];
    this.navigationImageLinks = [];
    this.globalOverlay = null;
  }

  connectedCallback() {
    this.cacheDomElements();
    this.setupDropdowns();
    this.setEventListeners();
  }

  cacheDomElements() {
    this.menuItems = Array.from(this.querySelectorAll(".desktop-menu__link"));
    this.topLevelParents = this.menuItems.filter((item) => {
      item.classList.contains("desktop-menu__link--first");
    });
    this.dropdownToggle = Array.from(
      this.querySelectorAll("[js-desktop-menu='dropdownToggle']"),
    );
    this.navigationImageLinks = Array.from(
      this.querySelectorAll("[js-navigation-image-link]"),
    );
    this.globalOverlay = document.querySelector(
      ".desktop-menu__overlay[js-desktop-menu='overlay-global']",
    );
  }

  setupDropdowns() {
    ["click", "focusin", "mouseenter"].forEach((interaction) => {
      this.topLevelParents.forEach((link) => {
        link.addEventListener(interaction, (event) => {
          event.preventDefault();
          if (link.classList.contains("has-children")) {
            const menuItem = link.closest(".desktop-menu__item");
            if (menuItem) {
              this.closeOtherDropdowns(menuItem);
              menuItem.classList.toggle("is-open");
              this.toggleGlobalOverlay();
              this.updateHeaderBackground();
            }
          }
        });
      });
    });
  }

  toggleGlobalOverlay() {
    if (!this.globalOverlay) return;
    const isAnyMenuOpen = this.querySelector(".desktop-menu__item.is-open");
    if (isAnyMenuOpen) {
      this.globalOverlay.style.opacity = "1";
      this.globalOverlay.style.pointerEvents = "auto";
    } else {
      this.globalOverlay.style.opacity = "0";
      this.globalOverlay.style.pointerEvents = "none";
    }
  }

  updateHeaderBackground() {
    const header = document.querySelector(
      ".header-background, .header-background-transparent",
    );
    const isAnyMenuOpen = this.querySelector(".desktop-menu__item.is-open");
    const overlay = document.querySelector(
      '.desktop-menu__overlay[js-desktop-menu="overlay-global"]',
    );

    if (header) {
      if (isAnyMenuOpen) {
        setTimeout(() => {
          document.body.classList.add("menu-dropdown-active");
        }, 0);
      } else {
        document.body.classList.remove("menu-dropdown-active");
      }

      // Always remove both margin classes first
      if (overlay) {
        overlay.classList.remove(
          "desktop-menu__overlay--no-margin",
          "desktop-menu__overlay--negative-margin",
        );
        if (header.classList.contains("header-background")) {
          overlay.classList.add("desktop-menu__overlay--no-margin");
        } else {
          overlay.classList.add("desktop-menu__overlay--negative-margin");
        }
      }
    }
  }

  setEventListeners(): void {
    this.dropdownToggle.forEach((toggle) => {
      toggle.addEventListener("click", () => {
        this.handleDropdownToggleClick(toggle);
      });
    });

    this.navigationImageLinks.forEach((link) => {
      link.addEventListener("click", () => {
        this.untoggleNavigationImageLinks();

        link.classList.toggle("is-active");
        this.classList.toggle("show-level3");
      });
    });

    if (this.globalOverlay) {
      this.globalOverlay.addEventListener("click", () => {
        this.closeAllDropdowns();
      });
    }

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        this.closeAllDropdowns();
      }
    });
  }

  handleDropdownToggleClick(toggle: Element) {
    const menuItem = toggle.closest(".desktop-menu__item");

    if (!menuItem) {
      return;
    }

    const isMenuItemActive = menuItem.classList.contains("is-open");

    this.closeAccountOverlay();

    if (isMenuItemActive) {
      this.closeAllDropdowns();
      return;
    }

    // Close any other open modals/drawers before opening desktop menu
    const openModals = document.querySelectorAll(
      ".modal.is-open, .drawer.is-open, .search-input--desktop.is-active",
    );
    openModals.forEach((modal) => {
      if (
        modal.classList.contains("modal") ||
        modal.classList.contains("drawer")
      ) {
        const hideMethod =
          (modal as any).hideModal || (modal as any).hideDrawer;
        if (hideMethod) {
          hideMethod.call(modal);
        }
      }
    });

    menuItem.classList.add("is-open");
    lock(this);
    this.toggleGlobalOverlay();
    this.updateHeaderBackground();

    this.closeOtherDropdowns(menuItem);

    const menuLevel3 = menuItem.querySelector(
      ".desktop-menu__children--level-3",
    );

    if (!menuLevel3) {
      return;
    }

    (menuLevel3 as HTMLElement).focus();
  }

  closeOtherDropdowns(currentItem: Element) {
    const menuItems = this.querySelectorAll(".desktop-menu__item");

    menuItems.forEach((item) => {
      if (item !== currentItem) {
        item.classList.remove("is-open");
      }
    });

    this.untoggleNavigationImageLinks();
    this.toggleGlobalOverlay();
  }

  closeAllDropdowns() {
    const menuItems = this.querySelectorAll(".desktop-menu__item.is-open");

    menuItems.forEach((item) => {
      item.classList.remove("is-open");
    });

    this.untoggleNavigationImageLinks();
    this.toggleGlobalOverlay();
    this.updateHeaderBackground();

    clearBodyLocks();
  }

  closeAccountOverlay() {
    const accountOverlay = document.querySelector(
      "uwp-account-overlay[modal-name='account-overlay']",
    ) as IAccountOverlay;

    accountOverlay?.hideAccountOverlay();
  }

  untoggleNavigationImageLinks() {
    const activeNavigationImageLinks = this.querySelectorAll(
      "[js-navigation-image-link].is-active",
    );

    activeNavigationImageLinks.forEach((link) => {
      link.classList.remove("is-active");
    });

    this.classList.remove("show-level3");
  }
}

customElements.define("uwp-desktop-menu", DesktopMenu);
